import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Task } from '../entities/Task';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { Check, EntityHelper } from '../utils/entity-helper';
import { InFilter } from '../baseServices/filter.interface';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { GlobalConstants } from '../constant/constant';

@Injectable({
  providedIn: 'root',
})
export class TaskService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<Task>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(task: Task, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: task, who: who, what: what });
  }

  getEmitChangeCallback(task: Task, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTask + '_' + task.RecordId,
      fn: this.emitChange.bind(this, task, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Task], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTaskByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Task> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<Task>(StoreName.Task, onlineId, Task, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as Task;

    if (this.checkIsTaskSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTaskByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Task> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<Task>(StoreName.Task, recordId, Task, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as Task;

    if (this.checkIsTaskSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getTasksByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Task[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Task>(StoreName.Task, GlobalConstants.KEY_NAMES.Id, onlineIds, Task, transaction);
    let records = result as Task[];

    if (!includeDeleted) {
      records = result.filter(task => !this.checkIsTaskSoftDeleted(task));
    }

    return records;
  }

  async getTasksByProjectId(projectId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Task[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Task>(StoreName.Task, GlobalConstants.KEY_NAMES.ProjectId, projectId, Task, transaction);
    let records = result as Task[];

    if (!includeDeleted) {
      records = result.filter(task => !this.checkIsTaskSoftDeleted(task));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createTask(who: DataChangeEmitWho, recordToCreate: Task, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTaskCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Task, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTaskByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTask(who: DataChangeEmitWho, recordToUpdate: Task, skipLocalCopyValidation: boolean, localCopy: Task | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTaskByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTaskUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Task>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Task>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTask(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Task, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTaskByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateTask(task: Task, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTaskCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTaskUpdate(task: Task, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getAllActiveChargeableTasksByProjectId(projectId: number, expireComparisonDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Task[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const tasksForProject = await this.getTasksByProjectId(projectId, transaction, includeDeleted);
    const activeChargeableTasksForProject = tasksForProject.filter(task => this.checkIsTaskActive(task, expireComparisonDate) && this.checkIsTaskChargeable(task));
    return activeChargeableTasksForProject;
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertTaskFromSync(who: DataChangeEmitWho, recordToUpsert: Task, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTaskByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTask(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTask(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTaskSoftDeleted(record: Task): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: Task, b: Task): boolean {
    return a.Name === b.Name && a.Number === b.Number;
  }

  checkIsTaskActive(task: Task, expireComparisonDate: DateTimeType): boolean {
    const compareDate = DateTimeHelper.formatDateTimeAsDate(expireComparisonDate);
    return (!task.StartDate || DateTimeHelper.checkTimeIsAfter(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(task.StartDate), undefined, true))
      && (!task.EndDate || DateTimeHelper.checkTimeIsBefore(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(task.EndDate), undefined, true));
  }

  checkIsTaskChargeable(task: Task): Boolean {
    return task.TlinxChargeableFlag || task.TlinxChargeableFlag === null;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
